/**
 * DOOR tab — the new Portal UI (docs/plans/2026-09-host-event-edit/handoff, "Door").
 * A black radius-28 card: `N OF M IN` in cyan (from the door summary), the 220px
 * rainbow-ringed scanner square with the scan line. START SCANNING starts the camera
 * IN PLACE (design_handoff_door_scanner): the bracket and scan line fade out and the
 * rear camera fades into the same square, scanning with the door page's engine
 * (`useDoorScanner`) and flashing each result over the frame. Switching tabs unmounts
 * this and releases the camera. OPEN FULL DOOR goes to /host/events/:id/door.
 * CHECK IN BY NAME → an inline search over ticket holders that posts
 * /events/{id}/door/checkin and answers VERIFIED / ALREADY IN, and the JUST SCANNED
 * card (LIVE dot) from `summary.recent`.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import doorService from '../../services/doorService';
import { timeLabel } from '../../utils/surfaceFormat';
import { guestName, guestEmail, checkedInCount } from './hostOpsModel';
import { Card, SectionHead, Btn, RowText, Ava, Empty, SearchPill } from './kit';
import useDoorScanner from '../../hooks/useDoorScanner';
import DoorScannerCard, { btnDark } from '../door/DoorScannerCard';
import { StatusPill } from './HostGuestList';

const VERDICT = {
  ok: { label: 'VERIFIED', tone: 'green' },
  already: { label: 'ALREADY IN', tone: 'yellow' },
  invalid: { label: 'NOT ON THIS LIST', tone: 'redOutline' },
  wrong_event: { label: 'WRONG EVENT', tone: 'redOutline' },
};

const CARD_TITLE = { fontSize: 11, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase' };

export default function HostDoor({ eventId, applications = [], mobile = false, refreshKey = 0, onCheckedIn }) {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [notAllowed, setNotAllowed] = useState(false);
  const [byName, setByName] = useState(false);
  const [q, setQ] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [verdicts, setVerdicts] = useState({}); // { [applicationId]: { result, message } }

  const load = useCallback(async () => {
    if (!eventId) return;
    const r = await doorService.summary(eventId);
    if (r.success) { setSummary(r.data); setNotAllowed(false); }
    else if (/403|forbidden/i.test(String(r.error || ''))) setNotAllowed(true);
  }, [eventId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // A check-in (camera or by name) moves the count and the JUST SCANNED list.
  const onResult = useCallback((r) => {
    if (r.result !== 'ok') return;
    load();
    onCheckedIn?.(r);
  }, [load, onCheckedIn]);
  const scanner = useDoorScanner({ eventId, regionId: 'door-scan-region-tab', onResult });

  const holders = useMemo(() => applications.filter((a) => a.status === 'paid'), [applications]);
  const matches = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return holders.slice(0, 8);
    return holders.filter((a) => guestName(a).toLowerCase().includes(s) || guestEmail(a).toLowerCase().includes(s) || (a.guest?.handle || '').toLowerCase().includes(s)).slice(0, 12);
  }, [holders, q]);

  const checkIn = async (app) => {
    setBusyId(app.id);
    // Same path as a scan: the frame flashes, the counts refresh.
    const data = await scanner.checkIn(app.id);
    setBusyId(null);
    setVerdicts((v) => ({ ...v, [app.id]: data }));
  };

  const inCount = summary?.ticketsCheckedIn ?? holders.reduce((n, a) => n + checkedInCount(a), 0);
  const soldCount = summary?.ticketsSold ?? holders.reduce((n, a) => n + (a.quantity || 1), 0);
  const recent = summary?.recent || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }} data-testid="door-tab">
      <DoorScannerCard scanner={scanner} size="tab" counter={`${inCount} OF ${soldCount} IN`} pad={mobile ? '18px' : '26px'}>
        {notAllowed && <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: '#FCA5A5', textTransform: 'uppercase' }}>THE DOOR IS NOT YOURS TO RUN ON THIS EVENT.</span>}
        <button type="button" className="door-btn-dark" style={{ ...btnDark, fontSize: 12, letterSpacing: '.16em' }} onClick={() => setByName((b) => !b)} aria-expanded={byName}>CHECK IN BY NAME</button>
        <button
          type="button"
          onClick={() => navigate(`/host/events/${eventId}/door`)}
          style={{ alignSelf: 'center', paddingTop: 4, border: 'none', background: 'none', color: '#fff', fontFamily: 'inherit', fontSize: 10, fontWeight: 700, letterSpacing: '.14em', textDecoration: 'underline', cursor: 'pointer' }}
        >
          OPEN FULL DOOR
        </button>
      </DoorScannerCard>

      {byName && (
        <Card rise gap="14" testId="checkin-by-name">
          <SectionHead title="CHECK IN BY NAME" tag={`${holders.length} TICKET HOLDERS`} />
          <SearchPill small autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type a name or email…" aria-label="Search ticket holders" />
          {holders.length === 0 ? (
            <Empty bare title="NO TICKETS SOLD YET." />
          ) : matches.length === 0 ? (
            <Empty bare title="NO ONE MATCHES." />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {matches.map((app) => {
                const name = guestName(app);
                const qty = app.quantity || 1;
                const inN = checkedInCount(app);
                const v = verdicts[app.id];
                const verdict = v ? (VERDICT[v.result] || VERDICT.invalid) : null;
                const allIn = inN >= qty;
                return (
                  <div key={app.id} className="hev-line" data-testid={`checkin-row-${app.id}`}>
                    <Ava name={name} imageUrl={app.guest?.avatarUrl} size={36} />
                    <RowText title={name} meta={`${qty} ${qty === 1 ? 'ticket' : 'tickets'} · ${inN === 0 ? 'not here yet' : allIn ? 'in' : `${inN} of ${qty} in`}${guestEmail(app) ? ` · ${guestEmail(app)}` : ''}`} plainMeta />
                    {verdict && <StatusPill tone={verdict.tone} title={v?.message || undefined}>{verdict.label}</StatusPill>}
                    {!allIn && (
                      <Btn variant="primary" size="sm" onClick={() => checkIn(app)} disabled={busyId === app.id} data-testid={`checkin-${app.id}`}>{busyId === app.id ? 'CHECKING…' : 'CHECK IN'}</Btn>
                    )}
                    {allIn && !verdict && <StatusPill tone="green">IN</StatusPill>}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      <Card rise gap="14" testId="just-scanned">
        <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <span style={CARD_TITLE}>JUST SCANNED</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 9, fontWeight: 700, letterSpacing: '0.16em', color: '#6B7280' }}>
            <span className="hev-pulse" data-green="" aria-hidden="true" />
            LIVE
          </span>
        </span>
        {recent.length === 0 ? (
          <Empty bare title="NOBODY SCANNED YET." />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {recent.map((s, i) => (
              <div key={`${s.applicationId}-${s.checkedInAt}-${i}`} className="hev-line">
                <Ava name={s.guestName || '?'} size={36} />
                <RowText title={s.guestName || 'Guest'} meta={`${s.quantity || 1} ${(s.quantity || 1) === 1 ? 'ticket' : 'tickets'}${s.tierName ? ` · ${s.tierName}` : ''}`} plainMeta />
                {s.result && s.result !== 'ok' && s.result !== 'already'
                  ? <StatusPill tone="redOutline">REJECTED</StatusPill>
                  : <StatusPill tone="green">IN</StatusPill>}
                <span style={{ flexShrink: 0, textAlign: 'right', fontSize: 11, color: '#9CA3AF', width: mobile ? undefined : 64 }}>{timeLabel(s.checkedInAt)}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
